import { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-toastify';

import validator from 'validator';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

import Loader from '../Loader/Loader';

import phonePrefixList from '../../data/prefixes.json';

import './SubmitForm.css';

const minBirthDate = new Date();
minBirthDate.setFullYear(minBirthDate.getFullYear() - 16);

const SubmitForm = ({ bookDone, data }) => {
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    postalCode: '',
    dateOfBirth: null,
  });

  const [phonePrefix, setPhonePrefix] = useState('+44');

  const [consentContact, setConsentContact] = useState(false);
  const [consentDataPolicy, setConsentDataPolicy] = useState(false);

  const [validations, setValidations] = useState({
    isNameValid: false,
    isLastNameValid: false,
    isEmailValid: false,
    isPhoneValid: false,
    isStreetValid: false,
    isCityValid: false,
    isPostalCodeValid: false,
  });

  const [error, setError] = useState(false);
  const [send, setSend] = useState(false);

  const isFormComplete =
    Object.values(formData).every((value) => value !== '' && value !== null) &&
    Object.values(validations).every((valid) => valid);

  const validatePhone = () => {
    let phoneValue = formData.phone.trim();
    if (!phoneValue) {
      setValidations((prev) => ({
        ...prev,
        isPhoneValid: false,
      }));
      return;
    }

    const fullNumber = phonePrefix + phoneValue;

    const phoneNumber = parsePhoneNumberFromString(fullNumber);
    const validNumber = phoneNumber ? phoneNumber.isValid() : false;

    setValidations((prev) => ({
      ...prev,
      isPhoneValid: validNumber,
    }));
  };

  useEffect(() => {
    validatePhone();
  }, [phonePrefix, formData.phone]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      const sanitizedValue = value.replace(/\D/g, '');
      setFormData((prev) => ({ ...prev, [name]: sanitizedValue }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === 'name') {
      setValidations((prev) => ({
        ...prev,
        isNameValid: value.trim().length >= 4,
      }));
    }
    if (name === 'lastName') {
      setValidations((prev) => ({
        ...prev,
        isLastNameValid: value.trim().length >= 4,
      }));
    }
    if (name === 'email') {
      setValidations((prev) => ({
        ...prev,
        isEmailValid: validator.isEmail(value),
      }));
    }

    if (name === 'street') {
      setValidations((prev) => ({
        ...prev,
        isStreetValid: value.trim() !== '',
      }));
    }
    if (name === 'city') {
      setValidations((prev) => ({ ...prev, isCityValid: value.trim() !== '' }));
    }
    if (name === 'postalCode') {
      setValidations((prev) => ({
        ...prev,
        isPostalCodeValid: value.trim() !== '',
      }));
    }
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({ ...prev, dateOfBirth: date }));
  };

  const handleSubmit = async () => {
    setSend(true);
    const fullPhone = phonePrefix + formData.phone;
    try {
      const response = await fetch(
        '/server/zoho_booking_custom_ui_function/create-appointment',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            first_name: formData.name,
            last_name: formData.lastName,
            email: formData.email,
            phone: fullPhone,
            street: formData.street,
            city: formData.city,
            postal_code: formData.postalCode,
            date_of_birth: formData.dateOfBirth.toISOString().split('T')[0],
            consent_contact: consentContact,
            consent_data_policy: consentDataPolicy,
            slot_id: data?.date.split(',')[2].trim(),
          }),
        }
      );

      const dataResponse = await response.json();
      const { code, result } = dataResponse;
      let parsedResult;

      try {
        parsedResult = JSON.parse(result);
      } catch (parseError) {
        console.error('Error parsing result:', parseError);
        toast.error('Unexpected response format', {
          position: 'top-center',
          autoClose: 5000,
          theme: 'colored',
        });
        return;
      }

      if (code === 3000 && parsedResult.status === 'success') {
        bookDone(data, formData.email, formData.name);
      } else if (code === 3000 && parsedResult.status === 'failure') {
        toast.error(`${parsedResult.data}`, {
          position: 'top-center',
          autoClose: 5000,
          theme: 'colored',
        });
      } else {
        toast.error('Something went wrong!', {
          position: 'top-center',
          autoClose: 5000,
          theme: 'colored',
        });
      }
    } catch (err) {
      toast.error('Something went wrong!', {
        position: 'top-center',
        autoClose: 5000,
        theme: 'colored',
      });
    } finally {
      setSend(false);
    }
  };

  const getValidationClass = (field) => {
    if (!formData[field] || formData[field].toString().trim() === '') return '';
    switch (field) {
      case 'name':
        return validations.isNameValid ? 'is-valid' : 'is-invalid';
      case 'lastName':
        return validations.isLastNameValid ? 'is-valid' : 'is-invalid';
      case 'email':
        return validations.isEmailValid ? 'is-valid' : 'is-invalid';
      case 'phone':
        return validations.isPhoneValid ? 'is-valid' : 'is-invalid';
      case 'street':
        return validations.isStreetValid ? 'is-valid' : 'is-invalid';
      case 'city':
        return validations.isCityValid ? 'is-valid' : 'is-invalid';
      case 'postalCode':
        return validations.isPostalCodeValid ? 'is-valid' : 'is-invalid';
      default:
        return '';
    }
  };

  if (error) return <h1>Something went wrong!</h1>;

  return (
    <>
      {send && (
        <div className="overlaySend">
          <div className="loadingContainer">
            <Loader />
          </div>
        </div>
      )}

      <div className="text-center">
        <h6 style={{ fontSize: '0.9rem', color: 'red' }} className="mt-2 mb-4">
          Step 3: Please Enter Your Details
        </h6>
      </div>

      <div className="row">
        <div className="col-md-2"></div>
        <div className="col-md-8">
          <div className="row">
            {['name', 'lastName'].map((field) => (
              <div className="col-md-6" key={field}>
                <div className="mb-3">
                  <label className="form-label">
                    {field === 'name' ? 'First Name' : 'Last Name'}{' '}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    name={field}
                    value={formData[field]}
                    onChange={handleChange}
                    type="text"
                    className={`form-control ${getValidationClass(field)}`}
                    placeholder={field === 'name' ? 'First Name' : 'Last Name'}
                  />
                </div>
              </div>
            ))}
          </div>

          {['email', 'street', 'city', 'postalCode'].map((field) => (
            <div className="mb-3" key={field}>
              <label className="form-label">
                {field === 'postalCode'
                  ? 'Postal Code'
                  : field.charAt(0).toUpperCase() + field.slice(1)}{' '}
                <span className="text-danger">*</span>
              </label>
              <input
                name={field}
                value={formData[field]}
                onChange={handleChange}
                type="text"
                className={`form-control ${getValidationClass(field)}`}
                placeholder={
                  field === 'postalCode'
                    ? 'Postal Code'
                    : field.charAt(0).toUpperCase() + field.slice(1)
                }
              />
            </div>
          ))}

          <div className="mb-3">
            <label className="form-label">
              Phone <span className="text-danger">*</span>
            </label>
            <div className="input-group">
              <select
                className="form-select phone-prefix"
                value={phonePrefix}
                onChange={(e) => setPhonePrefix(e.target.value)}
              >
                {phonePrefixList.data.map((prefix) => (
                  <option key={prefix.code} value={prefix.code}>
                    {prefix.country} ({prefix.code})
                  </option>
                ))}
              </select>
              <input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                type="text"
                className={`form-control ${getValidationClass('phone')}`}
                placeholder="Enter phone number"
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">
              Date of Birth <span className="text-danger">*</span>
            </label>
            <br />
            <DatePicker
              selected={formData.dateOfBirth || null}
              onChange={handleDateChange}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              className="form-control"
              placeholderText="Select Date of Birth"
              dateFormat="dd.MM.yyyy"
              maxDate={minBirthDate}
            />
          </div>

          <div className="mb-3">
            <div className="mb-3 mt-5">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  onChange={() => setConsentContact((prev) => !prev)}
                  checked={consentContact}
                />
                <label
                  style={{ userSelect: 'none' }}
                  className="form-check-label"
                  htmlFor="flexCheckDefault"
                >
                  <span className="text-danger">*</span> By ticking the
                  checkbox, you confirm that you have read and agree to the{' '}
                  <a
                    href="https://www.crystalandco.co.uk/terms-and-conditions"
                    target="__blank"
                    rel="noreferrer"
                  >
                    {' '}
                    Terms and Conditions
                  </a>{' '}
                  and that you consent to receive marketing communications as
                  outlined{' '}
                  <a
                    href="https://www.crystalandco.co.uk/terms-and-conditions"
                    target="__blank"
                    rel="noreferrer"
                  >
                    here
                  </a>
                  .
                </label>
              </div>

              <div className="form-check mt-4">
                <input
                  className="form-check-input"
                  type="checkbox"
                  onChange={() => setConsentDataPolicy((prev) => !prev)}
                  checked={consentDataPolicy}
                />
                <label
                  style={{ userSelect: 'none' }}
                  className="form-check-label"
                  htmlFor="flexCheckDefault"
                >
                  <span className="text-danger">*</span> I agree and I have been
                  informed about the personal{' '}
                  <a
                    href="https://www.crystalandco.co.uk/privacy-policy"
                    target="__blank"
                    rel="noreferrer"
                  >
                    data processing policy
                  </a>
                </label>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            className="btn btn-primary text-white mt-4"
            disabled={!isFormComplete || !consentContact || !consentDataPolicy}
          >
            Schedule Appointment
          </button>
        </div>
        <div className="col-md-2"></div>
      </div>
    </>
  );
};

export default SubmitForm;
